import { Component, ElementRef, HostBinding, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DialogReturnType } from '@prism/angular-shared';
import { Subject } from 'rxjs';
import { DialogHostDirective } from './dialog-host.directive';
import { DialogService } from './dialog.service';

@Component({
  selector: 'prism-dialog',
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
})
export class DialogComponent implements OnInit, OnDestroy {

  private submitPressed$: Subject<void> = new Subject()
  private previouslyFocusedElement: HTMLElement | null = null;



  @ViewChild(DialogHostDirective, { static: true }) dialogHost!: DialogHostDirective;

  constructor(
    public dialogService: DialogService<any>,
    public elementRef: ElementRef,
  ) { }

  ngOnInit(): void {
    this.previouslyFocusedElement = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    document.getElementById('dialog')?.setAttribute("style", `max-width:${this.dialogService.maxWidth}; height:${this.dialogService.height}`)

    this.recalculateDialogPosition()


    const component = this.dialogHost.viewContainerRef.createComponent(this.dialogService.component);

    // Adding two @Inputs that are added to each child accessable via:
    // @Input() submitPressed$!;
    // @Input() args!;
    component.instance.submitPressed$ = this.submitPressed$
    component.instance.args = this.dialogService.args

    setTimeout(() => {
      this.recalculateDialogPosition();
      if (!this.dialogRef.nativeElement.contains(document.activeElement)) {
        this.dialogRef.nativeElement.focus({ preventScroll: true });
      }
    });

  }

  ngOnDestroy(): void {
    this.previouslyFocusedElement?.focus();
  }

  @HostListener('window:resize')
  public windowResized() {
    // Browser viewport emulation and tablet rotation can report resize before
    // the dialog's percentage height has completed layout. Recalculate once
    // now and once on the next task so it cannot remain clipped.
    this.recalculateDialogPosition();
    setTimeout(() => this.recalculateDialogPosition());
  }

  public recalculateDialogPosition() {
    let elem = document.querySelector("#dialog") as HTMLDivElement

    const left = ((window.innerWidth / 2) - (elem.clientWidth / 2))
    elem.style.left = left >= 0 ? `${left}px` : "20px"
    const top = ((window.innerHeight / 2) - (elem.clientHeight / 2))
    elem.style.top = top >= 0 ? `${top}px` : "20px"
  }

  submit() {
    if (this.dialogService.submitButtonEnabled$.value) {
      this.submitPressed$.next()
    }
  }

  public secondary() {
    if (!this.dialogService.secondaryButtonEnabled$.value) return;
    this.dialogService.submit$.next({ value: null, type: DialogReturnType.SECONDARY })
  }


  closeDialog() {
    if (!this.dialogService.closeButtonEnabled$.value) return;
    this.dialogService.closeDialog()
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyPress(event: KeyboardEvent) {
    if (event.key === "Escape") {
      this.closeDialog()
      return;
    }
    if (event.key === 'Tab') {
      const focusable = this.focusableElements();
      if (!focusable.length) return;
      const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
      const nextIndex = event.shiftKey
        ? (currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1)
        : (currentIndex < 0 || currentIndex === focusable.length - 1 ? 0 : currentIndex + 1);
      event.preventDefault();
      focusable[nextIndex].focus();
    }
  }

  @ViewChild('dialog', { static: true }) dialogRef!: ElementRef<HTMLDivElement>;

  private focusableElements(): HTMLElement[] {
    return Array.from(this.dialogRef.nativeElement.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )).filter(element => element.offsetParent !== null);
  }

  private dragging = false;
  private startX = 0;
  private startY = 0;
  private startLeft = 0;
  private startTop = 0;

  private onDrag = (event: MouseEvent) => {
    if (!this.dragging) return;

    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;

    const dialog = this.dialogRef.nativeElement;

    dialog.style.left = `${this.startLeft + dx}px`;
    dialog.style.top = `${this.startTop + dy}px`;
  };

  private stopDrag = () => {
    this.dragging = false;
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
  };

  startDrag(event: MouseEvent) {
    event.preventDefault();
    if (!this.dialogService.draggable) return;


    const dialog = this.dialogRef.nativeElement;
    const rect = dialog.getBoundingClientRect();

    this.dragging = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startLeft = rect.left;
    this.startTop = rect.top;

    document.addEventListener('mousemove', this.onDrag);
    document.addEventListener('mouseup', this.stopDrag);
  }
}
